import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { logVitals, getVitals } from '../services/api';
import toast from 'react-hot-toast';

const FIELDS = [
  { key: 'weight_kg', label: 'Weight (kg)', type: 'number', step: '0.1', placeholder: '70.5' },
  { key: 'heart_rate_bpm', label: 'Resting Heart Rate (bpm)', type: 'number', placeholder: '65' },
  { key: 'systolic_bp', label: 'Systolic BP (mmHg)', type: 'number', placeholder: '120' },
  { key: 'diastolic_bp', label: 'Diastolic BP (mmHg)', type: 'number', placeholder: '80' },
  { key: 'blood_oxygen_pct', label: 'Blood Oxygen (%)', type: 'number', step: '0.1', placeholder: '98.5' },
  { key: 'sleep_hours', label: 'Sleep (hours)', type: 'number', step: '0.5', placeholder: '7.5' },
  { key: 'steps', label: 'Steps', type: 'number', placeholder: '8000' },
  { key: 'hrv_ms', label: 'HRV (ms)', type: 'number', placeholder: '45' },
  { key: 'temperature_c', label: 'Body Temp (°C)', type: 'number', step: '0.1', placeholder: '36.6' },
  { key: 'glucose_mmol', label: 'Blood Glucose (mmol/L)', type: 'number', step: '0.1', placeholder: '5.2' },
];

export default function VitalsPage() {
  const { currentUser } = useAuth();
  const [form, setForm] = useState<Record<string, string>>({});
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentVitals, setRecentVitals] = useState<any[]>([]);

  const loadRecent = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await getVitals(token, 7);
      setRecentVitals(res.vitals || []);
    } catch {/* ignore */}
  }, [currentUser]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const handleChange = (key: string, val: string) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const payload: Record<string, any> = { date, notes };
      FIELDS.forEach(({ key }) => {
        if (form[key]) payload[key] = parseFloat(form[key]);
      });
      await logVitals(token, payload);
      toast.success('Vitals saved!');
      setForm({});
      setNotes('');
      loadRecent();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save vitals');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Daily Vitals</h1>
        <p className="text-gray-500 text-sm mt-1">Log your health measurements for today.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {/* Metric fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(({ key, label, type, step, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <input
                type={type}
                step={step}
                value={form[key] || ''}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={placeholder}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          ))}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How are you feeling today? Any symptoms?"
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Saving...' : 'Save Vitals'}
        </button>
      </form>

      {/* Recent vitals table */}
      {recentVitals.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Recent (7 days)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs text-gray-500 font-medium">Date</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-500 font-medium">Weight</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-500 font-medium">HR</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-500 font-medium">BP</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-500 font-medium">Sleep</th>
                  <th className="px-4 py-2 text-right text-xs text-gray-500 font-medium">Steps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentVitals.map((v) => (
                  <tr key={v.date} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-700 font-medium">{v.date}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{v.weight_kg ? `${v.weight_kg} kg` : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{v.heart_rate_bpm ? `${v.heart_rate_bpm} bpm` : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">
                      {v.systolic_bp && v.diastolic_bp ? `${v.systolic_bp}/${v.diastolic_bp}` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{v.sleep_hours ? `${v.sleep_hours}h` : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{v.steps ? v.steps.toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
